import { useCallback, useEffect, useRef, useState } from "react";
import { Leaf, MapPin, Users, Volume2, VolumeX } from "lucide-react";
import { API_BASE } from "@/lib/api";
import DoctorAvatar from "@/components/DoctorAvatar";

export default function PublicQueue() {
  const [branches, setBranches] = useState([]);
  const [branchCode, setBranchCode] = useState(() => {
    return new URLSearchParams(window.location.search).get("b") || localStorage.getItem("badaris_public_branch") || "";
  });
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(Date.now());
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("badaris_queue_sound") === "1");
  const prevServingRef = useRef({}); // doctor_username -> last token
  const audioCtxRef = useRef(null);

  const enableSound = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      // brief silent resume unlocks autoplay
      audioCtxRef.current.resume?.();
      setSoundOn(true);
      localStorage.setItem("badaris_queue_sound", "1");
    } catch { /* ignore */ }
  };
  const disableSound = () => {
    setSoundOn(false);
    localStorage.setItem("badaris_queue_sound", "0");
  };

  const playChime = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    // Two-tone chime: 880Hz then 1320Hz
    [
      { f: 880, t: now, d: 0.28 },
      { f: 1320, t: now + 0.16, d: 0.32 },
    ].forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      osc.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      osc.start(t);
      osc.stop(t + d + 0.05);
    });
  };

  const speak = (text) => {
    if (!("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.92; u.pitch = 1.0; u.volume = 1.0;
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetch(`${API_BASE}/public/branches`)
      .then((r) => r.json())
      .then((rows) => {
        setBranches(rows);
        if (!branchCode && rows[0]) setBranchCode(rows[0].code);
      })
      .catch(() => {});
    // Only run once on mount — intentional
     
  }, []);

  const load = useCallback(async () => {
    if (!branchCode) return;
    try {
      const res = await fetch(`${API_BASE}/public/queue?branch_code=${branchCode}`);
      if (!res.ok) throw new Error();
      const json = await res.json();
      // Detect new calls per doctor
      const newlyServing = [];
      (json.rooms || []).forEach((r) => {
        const key = r.doctor_username;
        const token = r.now_serving?.token || null;
        const prev = prevServingRef.current[key];
        if (token && token !== prev) newlyServing.push({ token, doctor: r.doctor_name });
        prevServingRef.current[key] = token;
      });
      setData(json);
      if (soundOn && newlyServing.length && audioCtxRef.current) {
        playChime();
        setTimeout(() => {
          newlyServing.forEach(({ token, doctor }) => {
            speak(`Token ${token.replace(/-/g, " ")}, please proceed to ${doctor}`);
          });
        }, 700);
      }
    } catch {
      setData(null);
    }
  }, [branchCode, soundOn]);

  useEffect(() => {
    if (!branchCode) return;
    localStorage.setItem("badaris_public_branch", branchCode);
    load();
    const t = setInterval(load, 5000);
    const clockT = setInterval(() => setTick(Date.now()), 1000);
    return () => {
      clearInterval(t);
      clearInterval(clockT);
    };
  }, [branchCode, load]);

  const now = new Date(tick);
  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="min-h-screen bg-warm-sand" data-testid="public-queue-page">
      <header className="bg-forest text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-white/12 flex items-center justify-center">
              <Leaf className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] opacity-70">Badarinath Ayurveda Hospital</div>
              <div className="font-display text-2xl leading-tight">Queue · Now Serving</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={soundOn ? disableSound : enableSound}
              data-testid="queue-sound-toggle"
              className={`h-10 px-3 rounded-lg border font-semibold inline-flex items-center gap-2 text-sm ${
                soundOn ? "bg-white text-forest border-white" : "bg-white/10 text-white border-white/30 hover:bg-white/20"
              }`}
              title={soundOn ? "Sound on — click to mute" : "Turn on chime + voice call"}
            >
              {soundOn ? <Volume2 className="h-4 w-4" strokeWidth={1.75} /> : <VolumeX className="h-4 w-4" strokeWidth={1.75} />}
              {soundOn ? "Sound on" : "Turn on sound"}
            </button>
            <div className="text-right">
              <div className="font-display text-3xl leading-none tabular-nums">{timeStr}</div>
              <div className="text-xs opacity-80">{dateStr}</div>
            </div>
          </div>
        </div>

        {/* Branch selector */}
        <div className="max-w-7xl mx-auto px-6 pb-4">
          <div className="flex flex-wrap gap-2" data-testid="public-branch-picker">
            {branches.map((b) => (
              <button
                key={b.code}
                onClick={() => setBranchCode(b.code)}
                data-testid={`public-branch-${b.code}`}
                className={`h-9 px-3 rounded-lg border transition-colors text-sm font-semibold flex items-center gap-2 ${
                  branchCode === b.code
                    ? "bg-white text-forest border-white"
                    : "border-white/25 text-white hover:bg-white/10"
                }`}
              >
                <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />
                {b.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!data ? (
          <div className="text-center py-16 text-[#5C5652]">Loading queue…</div>
        ) : data.rooms.length === 0 ? (
          <div className="text-center py-16 max-w-md mx-auto">
            <div className="font-display text-3xl text-forest mb-2">
              No doctors are consulting at {data.branch.name} today
            </div>
            <p className="text-[#5C5652]">Please check back shortly or ask at reception.</p>
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="public-rooms-grid">
            {data.rooms.map((room) => {
              const nowServing = room.now_serving;
              return (
                <div
                  key={room.doctor_username}
                  data-testid={`room-${room.doctor_username}`}
                  className="rounded-lg bg-white border border-sand overflow-hidden"
                >
                  <div className="px-6 py-4 border-b border-sand flex items-center gap-3">
                    <DoctorAvatar
                      doctor={{
                        username: room.doctor_username,
                        name: room.doctor_name,
                        avatar_data_url: room.avatar_data_url,
                      }}
                      size={48}
                      ring
                      testid={`public-avatar-${room.doctor_username}`}
                    />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Consulting Doctor</div>
                      <div className="font-display text-lg text-forest leading-tight">{room.doctor_name}</div>
                    </div>
                  </div>

                  <div className={`px-6 py-8 text-center ${nowServing ? "bg-[#1A4331]/[0.04]" : ""}`}>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#5C5652] mb-2">
                      Now serving
                    </div>
                    {nowServing ? (
                      <div
                        className="font-display text-6xl sm:text-7xl text-forest leading-none tabular-nums animate-fade-up"
                        key={nowServing.token}
                        data-testid={`now-serving-${room.doctor_username}`}
                      >
                        {nowServing.token}
                      </div>
                    ) : (
                      <div className="font-display text-4xl text-[#5C5652]/40 leading-none">—</div>
                    )}
                  </div>

                  <div className="px-6 py-4 bg-[#F9F6F0] border-t border-sand">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652] flex items-center gap-1.5">
                        <Users className="h-3 w-3" strokeWidth={2} />
                        Waiting · {room.waiting_count}
                      </div>
                    </div>
                    {room.waiting.length === 0 ? (
                      <div className="text-xs text-[#5C5652]/70">No patients waiting.</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5" data-testid={`waiting-${room.doctor_username}`}>
                        {room.waiting.map((w) => (
                          <span
                            key={w.token}
                            className="text-xs font-mono text-forest bg-white border border-sand px-2 py-1 rounded"
                          >
                            {w.token}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-xs text-[#5C5652]">
        Auto-refreshes every 5 seconds · Badaris Lifecare
      </footer>
    </div>
  );
}
